import { MessagePreview } from '@/components/message-preview';
import { SectionCard } from '@/components/section-card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { TestSendInput } from '@/components/test-send-input';
import { serializeForPreview } from '@/components/sms-editor/utils/serializer';
import { useStep } from '../../layout';
import { DesignStepSkeleton } from '@/components/ui/skeletons';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Info, CalendarIcon } from 'lucide-react';
import { Form, FormField, FormItem, FormMessage } from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format, isBefore, startOfDay, parse } from 'date-fns';
import {
  TIMEZONES,
  SUBSCRIBER_LOCAL_TIME,
  type TimezoneKey,
  type ScheduleType
} from '../../../constants/scheduling';
import { isQuietHours, generateTimeSlots, formatTimeDisplay } from '../../../utils/scheduling';
import { useForm, useFormState } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { BASE_INITIAL_VALUE, type CampaignVariantContent } from '@/components/sms-editor/types';
import { cn } from '@/lib/utils';
import { useCampaignWizard } from './../../context/campaign-wizard-context';
import {
  CampaignScheduling,
  SchedulingConfig,
  LocalTimeConfig,
  UniversalTimeConfig
} from '../../../types';
import { QuietHoursWarning } from './quiet-hours-warning';
import { TimezoneSelector } from './timezone-selector';
import {
  scheduleFormSchema,
  type ScheduleFormData,
  type SchedulingInputsProps
} from './form-schema';

function SchedulingInputs({ form }: SchedulingInputsProps) {
  const timeSlots = generateTimeSlots(form.watch('scheduling.date'));
  const isSubscriberLocal = form.watch('scheduling.timezone') === SUBSCRIBER_LOCAL_TIME;

  const handleTimezoneChange = (value: string) => {
    const currentScheduling = form.getValues('scheduling') as {
      date?: string;
      time?: string;
      pastTimezoneBehavior?: 'send_immediately' | 'send_next_day' | 'skip';
      unknownTimezoneBehavior?: 'use_fallback' | 'skip';
      fallbackTimezone?: TimezoneKey;
    } | null;

    const baseFields = {
      date: currentScheduling?.date ?? format(new Date(), 'yyyy-MM-dd'),
      time: currentScheduling?.time ?? ''
    };

    if (value === SUBSCRIBER_LOCAL_TIME) {
      form.setValue(
        'scheduling',
        {
          type: 'local',
          timezone: SUBSCRIBER_LOCAL_TIME,
          ...baseFields,
          pastTimezoneBehavior: currentScheduling?.pastTimezoneBehavior ?? 'send_immediately',
          unknownTimezoneBehavior: currentScheduling?.unknownTimezoneBehavior ?? 'use_fallback',
          fallbackTimezone: currentScheduling?.fallbackTimezone
        },
        {
          shouldValidate: true
        }
      );
    } else {
      form.setValue(
        'scheduling',
        {
          type: 'universal',
          timezone: value,
          ...baseFields
        },
        {
          shouldValidate: true
        }
      );
    }
  };

  return (
    <>
      <div className="grid grid-cols-3 gap-4">
        {/* Date Input */}
        <FormField
          control={form.control}
          name="scheduling.date"
          render={({ field }) => (
            <FormItem>
              <Popover>
                <PopoverTrigger asChild>
                  <div className="relative">
                    <Input
                      type="text"
                      value={field.value ?? ''}
                      placeholder="MM/DD/YYYY"
                      className="bg-input rounded-lg pr-10 cursor-pointer hover:bg-accent/40 hover:text-accent-foreground"
                      readOnly
                    />
                    <CalendarIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                  </div>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-2 rounded-lg" align="start" sideOffset={4}>
                  <Calendar
                    mode="single"
                    selected={
                      field.value
                        ? parse(field.value, 'yyyy-MM-dd', startOfDay(new Date()))
                        : undefined
                    }
                    onSelect={(date) => {
                      const dateStr = date ? format(date, 'yyyy-MM-dd') : '';
                      form.setValue('scheduling.date', dateStr, {
                        shouldValidate: true
                      });
                      form.setValue('scheduling.time', '', {
                        shouldValidate: true
                      });
                    }}
                    autoFocus
                    disabled={(date) => isBefore(date, startOfDay(new Date()))}
                  />
                </PopoverContent>
              </Popover>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Time Input */}
        <FormField
          control={form.control}
          name="scheduling.time"
          render={({ field }) => (
            <FormItem>
              <Select
                value={field.value}
                onValueChange={(value) => {
                  form.setValue('scheduling.time', value, {
                    shouldValidate: true
                  });
                }}
              >
                <SelectTrigger className="bg-input rounded-lg cursor-pointer hover:bg-accent/40 hover:text-accent-foreground">
                  <SelectValue placeholder="Select time">
                    {field.value && formatTimeDisplay(field.value)}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {timeSlots.map((slot) => (
                    <SelectItem
                      key={slot.value}
                      value={slot.value}
                      disabled={slot.disabled}
                      className={cn('cursor-pointer', slot.disabled && 'opacity-50')}
                    >
                      {formatTimeDisplay(slot.value)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Timezone Select */}
        <FormField
          control={form.control}
          name="scheduling.timezone"
          render={({ field }) => (
            <FormItem>
              <TimezoneSelector value={field.value} onValueChange={handleTimezoneChange} />
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      {/* Past Timezone Section */}
      {isSubscriberLocal && (
        <div className="mt-6 space-y-4">
          <div className="text-sm text-muted-foreground">
            The scheduled send time has passed in some subscribers&apos; timezones. What would you
            like to do for those subscribers?
          </div>

          <FormField
            control={form.control}
            name="scheduling.pastTimezoneBehavior"
            render={({ field }) => (
              <FormItem>
                <RadioGroup
                  value={field.value}
                  onValueChange={field.onChange}
                  className="space-y-3"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="send_immediately" id="send_immediately" />
                    <Label htmlFor="send_immediately">Send the message immediately</Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="send_next_day" id="send_next_day" />
                    <Label htmlFor="send_next_day">
                      Send the message at the scheduled time on the next day
                    </Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="skip" id="skip" />
                    <Label htmlFor="skip">Don&apos;t send to these subscribers</Label>
                  </div>
                </RadioGroup>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      )}

      {/* Unknown Timezone Section */}
      {isSubscriberLocal && (
        <div className="mt-6">
          <Alert className="bg-muted border-none">
            <Info className="h-4 w-4" />
            <AlertDescription className="mt-2 flex flex-col gap-4">
              <div>
                Please note that subscribers without a known timezone will have a timezone selected
                for them. If you have subscribers traveling or living internationally, sending could
                take up to 24 hours.
              </div>

              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="scheduling.unknownTimezoneBehavior"
                  render={({ field }) => (
                    <FormItem>
                      <RadioGroup
                        value={field.value}
                        onValueChange={field.onChange}
                        className="space-y-3"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="use_fallback" id="use_fallback" />
                          <Label htmlFor="use_fallback">Use fallback timezone</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="skip" id="skip_unknown" />
                          <Label htmlFor="skip_unknown">Skip these subscribers</Label>
                        </div>
                      </RadioGroup>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Fallback Timezone Selector */}
                {form.watch('scheduling.unknownTimezoneBehavior') === 'use_fallback' && (
                  <div className="ml-6">
                    <FormField
                      control={form.control}
                      name="scheduling.fallbackTimezone"
                      render={({ field }) => (
                        <FormItem>
                          <TimezoneSelector
                            value={field.value}
                            onValueChange={field.onChange}
                            excludeLocal={true}
                            className="w-[300px]"
                            placeholder="Select fallback timezone"
                          />
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                )}
              </div>
            </AlertDescription>
          </Alert>
        </div>
      )}

      {/* Show quiet hours warning only during quiet hours */}
      {(() => {
        const time = form.watch('scheduling.time');
        return time ? isQuietHours(time) && <QuietHoursWarning /> : null;
      })()}
    </>
  );
}

// Type guard functions
function isSchedulingConfig(value: unknown): value is SchedulingConfig {
  if (!value || typeof value !== 'object') return false;
  const config = value as Record<string, unknown>;
  return (
    typeof config.type === 'string' &&
    (config.type === 'local' || config.type === 'universal') &&
    config.config !== undefined
  );
}

function isCampaignScheduling(value: unknown): value is CampaignScheduling {
  if (!value || typeof value !== 'object') return false;
  const scheduling = value as Record<string, unknown>;
  return (
    typeof scheduling.type === 'string' &&
    ['draft', 'now', 'schedule'].includes(scheduling.type) &&
    (scheduling.config === undefined || isSchedulingConfig(scheduling.config))
  );
}

// SHOULD BE CALCULATED ON THE SERVER SIDE. JUST COMMENTED OUT FOR NOW.
// function calculateGeneratedAt(scheduling: NonNullable<ScheduleFormData['scheduling']>): string | undefined {
//   if (!scheduling.date || !scheduling.time) return undefined

//   try {
//     switch (scheduling.type) {
//       case 'universal':
//         return scheduling.timezone ? getUTCISOString(scheduling.date, scheduling.time, scheduling.timezone) : undefined
//       case 'local':
//         // For local time, always use EST as reference timezone
//         return getUTCISOString(scheduling.date, scheduling.time, 'America/New_York')
//       default:
//         return undefined
//     }
//   } catch (error) {
//     console.error('Error calculating generatedAt:', error)
//     return undefined
//   }
// }

// Move helper function outside the component for stable reference
const constructSchedulingConfig = (
  scheduling: NonNullable<ScheduleFormData['scheduling']>
): SchedulingConfig => {
  if (scheduling.type === 'local') {
    const config: LocalTimeConfig = {
      date: scheduling.date ?? '',
      time: scheduling.time ?? '',
      pastTimezoneBehavior: scheduling.pastTimezoneBehavior ?? 'send_immediately',
      unknownTimezoneBehavior: scheduling.unknownTimezoneBehavior ?? 'use_fallback',
      fallbackTimezone:
        scheduling.unknownTimezoneBehavior === 'use_fallback'
          ? (scheduling.fallbackTimezone as Exclude<TimezoneKey, typeof SUBSCRIBER_LOCAL_TIME>)
          : (Object.keys(TIMEZONES)[0] as Exclude<TimezoneKey, typeof SUBSCRIBER_LOCAL_TIME>)
    };
    return { type: 'local', config };
  }
  // Default to universal if not local (should match schema refinement)
  return {
    type: 'universal',
    config: {
      date: scheduling.date ?? '',
      time: scheduling.time ?? '',
      timezone: scheduling.timezone as Exclude<TimezoneKey, typeof SUBSCRIBER_LOCAL_TIME>
    }
  };
};

export function ReviewPage() {
  const { campaign, loading, updateScheduling } = useCampaignWizard();
  const { setStepValid } = useStep();
  const [selectedVariantId, setSelectedVariantId] = useState<string>('');

  // Set initial selected variant when campaign loads
  useEffect(() => {
    const variants = campaign?.variants;
    if (variants && variants.length > 0 && !selectedVariantId) {
      setSelectedVariantId(variants[0].variantId);
    }
  }, [campaign, selectedVariantId]);

  // Initialize form with current campaign values
  const form = useForm<ScheduleFormData>({
    resolver: zodResolver(scheduleFormSchema),
    mode: 'onChange',
    values: {
      scheduleType: isCampaignScheduling(campaign?.scheduled) ? campaign.scheduled.type : 'draft',
      scheduling:
        isCampaignScheduling(campaign?.scheduled) && campaign.scheduled.config
          ? {
              type: campaign.scheduled.config.type,
              ...(campaign.scheduled.config.type === 'local'
                ? {
                    // Parse date string correctly to maintain local midnight
                    date: (campaign.scheduled.config.config as LocalTimeConfig).date,
                    time: (campaign.scheduled.config.config as LocalTimeConfig).time,
                    timezone: SUBSCRIBER_LOCAL_TIME,
                    pastTimezoneBehavior: (campaign.scheduled.config.config as LocalTimeConfig)
                      .pastTimezoneBehavior,
                    unknownTimezoneBehavior: (campaign.scheduled.config.config as LocalTimeConfig)
                      .unknownTimezoneBehavior,
                    fallbackTimezone: (campaign.scheduled.config.config as LocalTimeConfig)
                      .fallbackTimezone
                  }
                : {
                    // Parse date string correctly to maintain local midnight
                    date: (campaign.scheduled.config.config as UniversalTimeConfig).date,
                    time: (campaign.scheduled.config.config as UniversalTimeConfig).time,
                    timezone: (campaign.scheduled.config.config as UniversalTimeConfig).timezone
                  })
            }
          : undefined
    }
  });

  const { isValid } = useFormState({
    control: form.control
  });

  useEffect(() => {
    setStepValid(isValid);
  }, [isValid, setStepValid]);

  useEffect(() => {
    const scheduleType = form.watch('scheduleType');
    const schedulingDetails = form.watch('scheduling');
    const isSchedulingDetailsValid = form.formState.isValid;

    switch (scheduleType) {
      case 'draft':
        void updateScheduling({ type: 'draft' });
        break;

      case 'now':
        void updateScheduling({ type: 'now' });
        break;

      case 'schedule':
        if (isSchedulingDetailsValid && schedulingDetails) {
          const config = constructSchedulingConfig(schedulingDetails);
          void updateScheduling({
            type: 'schedule',
            config: config
          });
        }
        break;
      default:
        console.warn('[ReviewPage] Unknown scheduleType encountered:', scheduleType);
    }

    // Dependencies: Include the type watcher, details watcher (for 'schedule' case logic),
    // validity state, the update function, and the stable config function.
  }, [
    form.watch('scheduleType'),
    form.watch('scheduling'),
    form.formState.isValid,
    updateScheduling,
    constructSchedulingConfig
  ]);

  const handleSendTest = (phoneNumber: string) => {
    console.log('Send test to', phoneNumber);
  };

  if (loading || !campaign) {
    return <DesignStepSkeleton />;
  }

  const selectedVariant = campaign.variants.find((v) => v.variantId === selectedVariantId);
  const variantContent = selectedVariant?.content as CampaignVariantContent | undefined;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex gap-4">
        <div className="w-[60%]">
          <Form {...form}>
            <SectionCard title="Schedule SMS">
              <div className="space-y-6">
                <div>
                  <Label className="text-base">When do you want to send this campaign?</Label>
                  <FormField
                    control={form.control}
                    name="scheduleType"
                    render={({ field }) => (
                      <FormItem>
                        <Select
                          value={field.value}
                          onValueChange={(value: ScheduleType) => {
                            field.onChange(value);
                          }}
                        >
                          <SelectTrigger className="mt-2 cursor-pointer bg-input hover:bg-accent/40 hover:text-accent-foreground">
                            <SelectValue placeholder="Select timing" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem className="cursor-pointer" value="draft">
                              {`Don't send, save as draft`}
                            </SelectItem>
                            <SelectItem className="cursor-pointer" value="now">
                              Send Now
                            </SelectItem>
                            <SelectItem className="cursor-pointer" value="schedule">
                              Schedule to send later
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {form.watch('scheduleType') === 'schedule' && (
                    <div className="mt-4">
                      <SchedulingInputs form={form} />
                    </div>
                  )}
                </div>
              </div>
            </SectionCard>
          </Form>
        </div>

        {/* Right column - preview (40%) */}
        <div className="w-[40%]">
          <SectionCard title="Preview">
            <div className="space-y-6">
              {/* Variant selector */}
              <div>
                <Label>Variant</Label>
                <Select value={selectedVariantId} onValueChange={setSelectedVariantId}>
                  <SelectTrigger className="mt-2 cursor-pointer bg-input hover:bg-accent/40 hover:text-accent-foreground">
                    <SelectValue placeholder="Select variant">
                      {selectedVariant?.variantName}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {campaign.variants.map((variant) => (
                      <SelectItem
                        key={variant.variantId}
                        value={variant.variantId}
                        className="cursor-pointer"
                      >
                        {variant.variantName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Phone Preview */}
              <div className="flex-1 flex items-center justify-center py-8">
                <MessagePreview
                  content={serializeForPreview(variantContent?.editorState ?? BASE_INITIAL_VALUE)}
                />
              </div>

              {/* Test Send Section */}
              <div className="space-y-2 flex flex-col items-center">
                <Label>Send Test Message</Label>
                <TestSendInput onSendTest={handleSendTest} />
              </div>
            </div>
          </SectionCard>
        </div>
      </div>
    </div>
  );
}
